import React, { useMemo } from 'react';
import { TransitionChopFill } from '@crabs-ai/web-icons';
import {
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_HORIZONTAL_PADDING,
} from '../constants';
import { getVisibleFrames } from '../utils/get-visible-frames';
import { getItemLeftOffset } from '../utils/position-utils';
import {
  useAllItems,
  useFps,
  useTimelineContext,
  useTracks,
} from '../utils/use-context';
import { useTransitionDropPreview } from './transition-drop-preview-provider';
import { remainOriginalTrackHeights } from './utils/drag/remain-original-track-heights';

interface TransitionDropPreviewProps {
  timelineWidth: number;
}

// 프리뷰 박스 크기
const PREVIEW_WIDTH = 48;
const PREVIEW_HEIGHT = 32;
const ICON_SIZE = 16;

export const TransitionDropPreview: React.FC<TransitionDropPreviewProps> = ({
  timelineWidth,
}) => {
  const { previewState } = useTransitionDropPreview();
  const { durationInFrames } = useTimelineContext();
  const { fps } = useFps();
  const { tracks } = useTracks();
  const { items } = useAllItems();

  const visibleFrames = useMemo(
    () =>
      getVisibleFrames({
        fps,
        totalDurationInFrames: durationInFrames,
      }),
    [fps, durationInFrames],
  );

  // 트랙 레이아웃 계산
  const tracksAndLayout = useMemo(() => {
    return remainOriginalTrackHeights({
      originalTracks: tracks,
      originalItems: items,
      newTracks: tracks,
      newItems: items,
    });
  }, [tracks, items]);

  if (!previewState) {
    return null;
  }

  const { edge, isAvailable } = previewState;

  // 경계 위치 계산
  const left = getItemLeftOffset({
    timelineWidth,
    totalDurationInFrames: visibleFrames,
    from: edge.edgeFrame,
  });

  // 트랙 위치 계산
  const trackLayout = tracksAndLayout[edge.trackIndex];
  if (!trackLayout) {
    return null;
  }

  const centerY = trackLayout.top + trackLayout.height / 2;

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-visible"
      style={{ zIndex: 101 }}
    >
      {/* 트랜지션 프리뷰 박스 - 타임라인 transition item과 동일한 디자인 */}
      <div
        className={isAvailable ? 'bg-sys-style-inverse-light' : 'bg-red-500/30'}
        style={{
          position: 'absolute',
          left: left + TIMELINE_HORIZONTAL_PADDING - PREVIEW_WIDTH / 2,
          top: TIMELINE_HEADER_HEIGHT + centerY - PREVIEW_HEIGHT / 2,
          width: PREVIEW_WIDTH,
          height: PREVIEW_HEIGHT,
          borderRadius: 4,
          border: isAvailable
            ? '1px solid var(--sys-style-inverse-light)'
            : '1px solid rgba(239, 68, 68, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        }}
      >
        {isAvailable ? (
          <TransitionChopFill
            width={ICON_SIZE}
            height={ICON_SIZE}
            className="[&_path]:fill-sys-style-inverse-light"
          />
        ) : (
          // 불가능할 때 X 아이콘
          <svg
            width={ICON_SIZE}
            height={ICON_SIZE}
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M6 6L18 18M6 18L18 6"
              stroke="#EF4444"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        )}
      </div>
    </div>
  );
};
